import React, { useState } from 'react';
import { Card, Row, Col, Typography, Button, Input, message, Spin, Alert, Divider, Upload, Space, Statistic } from 'antd';
import { QrcodeOutlined, CreditCardOutlined, UploadOutlined, CheckCircleFilled, WalletOutlined, CopyOutlined } from '@ant-design/icons';
import { paymentService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const QrPayment = () => {
  const [amount, setAmount] = useState('');
  const [orderId, setOrderId] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleGenerate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      message.warning('Please enter a valid amount');
      return;
    }
    setGenerating(true);
    try {
      const res = await paymentService.generateQrCode(parseFloat(amount));
      const data = res.data?.data || res.data;
      setQrCode(data.qr_code || data.qr_code_url || data);
      setSuccess(false);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmitProof = async (file) => {
    if (!orderId) {
      message.warning('Please enter an Order ID');
      return false;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('payment_proof', file);
      await paymentService.submitPaymentProof(orderId, formData);
      message.success('Payment proof submitted! Awaiting verification.');
      setSuccess(true);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to submit proof');
    } finally {
      setSubmitting(false);
    }
    return false;
  };

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>QR Payment</Title>
        <Paragraph>Generate a QR code for payment and submit your payment proof.</Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card className="customer-card" title={<span><QrcodeOutlined /> Generate QR Code</span>}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <WalletOutlined style={{ fontSize: 28, color: '#52b788' }} />
              </div>
              <Title level={4} style={{ color: '#2d5a3d' }}>Request Payment QR</Title>
              <Paragraph type="secondary">Enter the amount you need to pay and generate a QR code for scanning.</Paragraph>
            </div>
            <Input prefix="NPR" type="number" placeholder="Enter amount" value={amount} onChange={(e) => setAmount(e.target.value)} size="large" style={{ borderRadius: 10, marginBottom: 12, height: 48, fontSize: 18 }} />
            <Button type="primary" block size="large" onClick={handleGenerate} loading={generating}
              style={{ background: 'linear-gradient(135deg, #52b788, #2d5a3d)', border: 'none', borderRadius: 10, fontWeight: 600, height: 48 }}>
              <QrcodeOutlined /> Generate QR Code
            </Button>
          </Card>

          {qrCode && (
            <Card className="customer-card" style={{ marginTop: 16, textAlign: 'center' }}>
              <Title level={5}>Scan to Pay</Title>
              <img src={qrCode} alt="Payment QR Code" style={{ width: 200, height: 200, borderRadius: 12, border: '2px solid #e8f0e8' }} />
              <Paragraph style={{ marginTop: 12 }}>Amount: <Text strong style={{ color: '#52b788', fontSize: 18 }}>NPR {parseFloat(amount).toFixed(2)}</Text></Paragraph>
            </Card>
          )}
        </Col>

        <Col xs={24} md={12}>
          <Card className="customer-card" title={<span><UploadOutlined /> Submit Payment Proof</span>}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef4e3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <UploadOutlined style={{ fontSize: 28, color: '#f1a134' }} />
              </div>
              <Title level={4} style={{ color: '#2d5a3d' }}>Confirm Your Payment</Title>
              <Paragraph type="secondary">After making the payment, upload the screenshot or transaction receipt here.</Paragraph>
            </div>
            <Input placeholder="Order ID" value={orderId} onChange={(e) => setOrderId(e.target.value)} size="large" style={{ borderRadius: 10, marginBottom: 12 }} />
            <Upload beforeUpload={handleSubmitProof} showUploadList={false} accept="image/*" disabled={submitting}>
              <Button type="primary" block size="large" loading={submitting}
                style={{ background: 'linear-gradient(135deg, #f1a134, #e08a10)', border: 'none', borderRadius: 10, fontWeight: 600, height: 48 }}>
                <UploadOutlined /> Upload Payment Proof
              </Button>
            </Upload>
            {success && <Alert message="Payment proof submitted successfully!" type="success" showIcon style={{ marginTop: 12 }} />}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default QrPayment;
